import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Skeleton } from '@/components/ui';
import { formatDate } from '@/lib/utils';
import {
  buildRenewalTimeline,
  deriveEventType,
  deriveSkeletonEvents,
  filterEventsWithTimestamp,
  formatChargeAmount,
  formatEventCount,
  isPartialHistory,
} from '@/lib/utils/subscription-billing';
import type {
  DerivedRenewalEvent,
  ServicePriceInfo,
} from '@/lib/utils/subscription-billing';
import type {
  AdminSubscription,
  RenewalEvent,
  SubscriptionRenewalsResponse,
} from '@/types/admin-subscription';

// Spec 0222 §10: Rendering-Cap — der Endpoint liefert max. 200 Events,
// wir verlassen uns nicht darauf (Backfill kann das Volumen aendern).
const MAX_RENDERED_EVENTS = 200;

interface SubscriptionRenewalsTimelineProps {
  renewals: SubscriptionRenewalsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /**
   * Listen-Objekt (D3): renewalCount fuer die Partial-Erkennung,
   * createdAt/lastRenewalAt/terminatedAt/status fuer SKELETON-0283.
   */
  subscription: AdminSubscription;
  /** Service-Preis aus dem Join (B7) — Betragsschaetzung der Skeleton-Events */
  servicePrice?: ServicePriceInfo;
}

/**
 * Abrechnungshistorie-Timeline (Spec 0222 §5 + Skeleton Spec 283).
 * Zustaende: Loading, Empty, Partial, Truncated, Skeleton. Fehler beim Fetch
 * beeintraechtigen die restliche Detail-Card nicht (eigener Error-Block) und
 * werden NIE durch Synthese maskiert.
 */
export function SubscriptionRenewalsTimeline({
  renewals,
  isLoading,
  isError,
  onRetry,
  subscription,
  servicePrice,
}: SubscriptionRenewalsTimelineProps) {
  const { t } = useTranslation('subscriptions');

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="renewals-loading">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-5 w-3/4" />
      </div>
    );
  }

  if (isError || !renewals) {
    return (
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span>{t('renewals.error')}</span>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('renewals.retry')}
        </Button>
      </div>
    );
  }

  // G1-Filter: Events ohne Timestamp rendern nicht und zaehlen nicht
  const realEvents = filterEventsWithTimestamp(renewals.renewals);
  // SKELETON-0283: nur nach erfolgreichem Fetch (Fehler-Fall ist oben raus)
  const skeleton = deriveSkeletonEvents(subscription, renewals.renewals, servicePrice);

  if (realEvents.length === 0 && skeleton.length === 0) {
    // TICK-001: Belastungen laut Listen-renewalCount, aber keine Events und
    // keine Synthese moeglich (z.B. createdAt fehlt) — bestehender Hinweis.
    if (isPartialHistory(subscription.renewalCount, realEvents)) {
      return (
        <p
          className="text-sm text-muted-foreground"
          data-testid="renewals-empty-partial-hint"
        >
          ⓘ {t('renewals.emptyPartialHint')}
        </p>
      );
    }
    return (
      <p className="text-sm text-muted-foreground">{t('renewals.empty')}</p>
    );
  }

  const partial = isPartialHistory(subscription.renewalCount, realEvents);
  const truncated = renewals.truncated === true;
  const entries = buildRenewalTimeline(renewals.renewals, skeleton).slice(
    0,
    MAX_RENDERED_EVENTS
  );

  return (
    <div className="space-y-2">
      {skeleton.length > 0 ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="renewals-reconstructed-hint"
        >
          ⓘ {t('renewals.derived.hint')}
        </p>
      ) : (
        partial && (
          <p className="text-sm text-muted-foreground" data-testid="renewals-partial-hint">
            ⓘ {t('renewals.partialHint')}
          </p>
        )
      )}
      {truncated && (
        <p className="text-sm text-muted-foreground" data-testid="renewals-truncated-hint">
          {t('renewals.truncatedHint')}
        </p>
      )}

      <ul className="max-h-80 space-y-1.5 overflow-y-auto pr-1">
        {entries.map((entry) =>
          entry.source === 'facade' ? (
            <TimelineRow
              key={`${entry.event.transactionId}-${entry.event.processedAt}`}
              event={entry.event}
            />
          ) : (
            <DerivedTimelineRow key={`derived-${entry.kind}`} entry={entry} />
          )
        )}
      </ul>
    </div>
  );
}

function TimelineRow({ event }: { event: RenewalEvent }) {
  const { t } = useTranslation('subscriptions');
  const eventType = deriveEventType(event);
  // K2: TXN-Link nur beim create-Event; InternetQ-ID (IQDE_) nur als Tooltip
  const txnLink = eventType === 'create' ? event.originalTransactionId : undefined;

  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm"
      title={event.transactionId}
    >
      <span aria-hidden className="text-muted-foreground">●</span>
      <span className="tabular-nums">{formatDate(event.processedAt)}</span>
      {eventType && (
        <Badge variant={eventType === 'create' ? 'default' : 'secondary'}>
          {eventType === 'create' ? t('renewals.badgeCreate') : t('renewals.badgeRenewal')}
        </Badge>
      )}
      <span className="tabular-nums">{formatChargeAmount(event.amount, event.currency)}</span>
      <span className="text-muted-foreground">{event.operatorId ?? '—'}</span>
      {txnLink && (
        <Link
          to={`/transactions/${txnLink}`}
          className="font-mono text-xs text-primary hover:underline"
        >
          {txnLink} ↗
        </Link>
      )}
    </li>
  );
}

/**
 * SKELETON-0283: abgeleitete Zeile — gedaempfte Optik, Badge "rekonstruiert",
 * Betraege nur als Schaetzung („≈"). Das Aggregat ist bewusst eine reine
 * Textzeile (nicht expandierbar, kein Link).
 */
function DerivedTimelineRow({ entry }: { entry: DerivedRenewalEvent }) {
  const { t } = useTranslation('subscriptions');

  const kindLabel =
    entry.kind === 'initial'
      ? t('renewals.badgeCreate')
      : entry.kind === 'last'
        ? t('renewals.derived.last')
        : entry.kind === 'termination'
          ? t('renewals.derived.termination')
          : undefined;

  const amountText =
    entry.kind === 'termination'
      ? undefined
      : entry.estimatedAmount != null
        ? `≈ ${formatChargeAmount(entry.estimatedAmount, entry.currency)}`
        : t('renewals.derived.amountUnknown');

  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-muted-foreground"
      data-testid={`derived-event-${entry.kind}`}
    >
      <span aria-hidden>○</span>
      {entry.kind === 'aggregate' ? (
        <>
          <span className="tabular-nums">
            {entry.periodEnd
              ? t('renewals.derived.aggregatePeriod', {
                  from: formatDate(entry.periodStart),
                  to: formatDate(entry.periodEnd),
                })
              : t('renewals.derived.aggregateSince', {
                  from: formatDate(entry.periodStart),
                })}
          </span>
          <span>
            {t('renewals.derived.aggregateLabel', {
              count: entry.count ?? 0,
              n: formatEventCount(entry.count ?? 0),
            })}
          </span>
        </>
      ) : (
        <>
          <span className="tabular-nums">{formatDate(entry.occurredAt)}</span>
          {kindLabel && <Badge variant="secondary">{kindLabel}</Badge>}
        </>
      )}
      {amountText && <span className="tabular-nums">{amountText}</span>}
      {entry.kind === 'initial' && entry.initialTransactionId && (
        <Link
          to={`/transactions/${entry.initialTransactionId}`}
          className="font-mono text-xs text-primary hover:underline"
        >
          {entry.initialTransactionId} ↗
        </Link>
      )}
      <Badge variant="outline" className="text-muted-foreground">
        {t('renewals.derived.badge')}
      </Badge>
    </li>
  );
}
